/**
 * "What's this?" drawer for parent-group-product-card.
 * Populates the shared #WhatsThisDrawer (drawer-element) with product images
 * and description, then opens it using the theme's native drawer system.
 *
 * Image pagination uses the theme's slider-element + slider-dots custom elements
 * (same as the mobile product gallery). A unique ID is generated per open so
 * the aria-controls wiring is correct even if the drawer is opened multiple times.
 */
(() => {
  const getDrawer = () => document.getElementById('WhatsThisDrawer');

  let sliderCounter = 0;

  const wireSlider = (contentEl) => {
    const sliderEl = contentEl.querySelector('.whats-this-drawer__slider');
    const dotsEl = contentEl.querySelector('.whats-this-drawer__dots');
    if (!sliderEl) return;

    // Give the slider a unique ID so slider-dots can reference it via aria-controls.
    const sliderId = 'WhatsThisSlider-' + (++sliderCounter);
    sliderEl.id = sliderId;
    if (dotsEl) dotsEl.setAttribute('aria-controls', sliderId);

    // Mouse drag-to-scroll (touch/trackpad use native CSS scroll).
    // Prevent the browser from initiating a native image/element drag.
    sliderEl.addEventListener('dragstart', (e) => e.preventDefault());

    let dragStartX = 0;
    let dragScrollLeft = 0;

    sliderEl.addEventListener('mousedown', (e) => {
      dragStartX = e.pageX;
      dragScrollLeft = sliderEl.scrollLeft;
      sliderEl.classList.add('is-dragging');
    });

    const onMouseMove = (e) => {
      if (!sliderEl.classList.contains('is-dragging')) return;
      sliderEl.scrollLeft = dragScrollLeft - (e.pageX - dragStartX);
    };

    const onMouseUp = () => {
      if (!sliderEl.classList.contains('is-dragging')) return;
      sliderEl.classList.remove('is-dragging');
      // Snap to nearest slide.
      const index = Math.round(sliderEl.scrollLeft / sliderEl.clientWidth);
      sliderEl.scrollTo({ left: index * sliderEl.clientWidth, behavior: 'smooth' });
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const openWhatsThis = (trigger) => {
    const drawer = getDrawer();
    if (!drawer) return;

    const tooltip = trigger.closest('.pgpc-tooltip');
    const template = tooltip?.querySelector('.whats-this-drawer-template');
    if (!template) return;

    const contentEl = drawer.querySelector('#WhatsThisDrawerContent');
    if (contentEl) {
      contentEl.innerHTML = '';
      contentEl.appendChild(template.content.cloneNode(true));
      wireSlider(contentEl);
    }

    drawer.show(trigger);
  };

  const initTooltip = (tooltip) => {
    if (tooltip.dataset.pgpcInitialized === 'true') return;

    const trigger = tooltip.querySelector('[data-whats-this-trigger]');
    if (!trigger) return;

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openWhatsThis(trigger);
    });

    tooltip.dataset.pgpcInitialized = 'true';
  };

  const initTooltips = () => document.querySelectorAll('.pgpc-tooltip').forEach(initTooltip);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTooltips);
  } else {
    initTooltips();
  }

  document.addEventListener('recommendations:loaded', initTooltips, true);

  const observer = new MutationObserver((mutations) => {
    const hasNewTooltips = mutations.some(({ addedNodes }) =>
      [...addedNodes].some(
        (node) =>
          node.nodeType === 1 &&
          ((node.classList?.contains('pgpc-tooltip') && node.dataset.pgpcInitialized !== 'true') ||
            node.querySelector?.('.pgpc-tooltip'))
      )
    );
    if (hasNewTooltips) initTooltips();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
